/**
 * The label on a conditional edge.
 *
 * Shared by the straight and the detoured edge renderers so the two cannot drift.
 * Three things are shown, and each is on the LINE rather than on a card because each
 * describes the TRANSITION rather than either question:
 *
 *  - ORDER (①②③). `nextQuestionRules` is ordered and the engine takes the first rule
 *    that evaluates true, so two edges out of one node are not symmetric. This was
 *    invisible on the canvas, and the editor is what makes it dangerous: "add a branch"
 *    appends a `true` catch-all, after which position decides whether a branch can
 *    ever fire. Only rendered when the node has more than one conditional edge —
 *    numbering a lone branch is noise.
 *  - CONDITION in words (`是`, `否则`, `含「SAP / Oracle」`) instead of
 *    `metric['Q_1_1_4'] === 'a'`. The raw expression stays available on hover; the
 *    long ones (the TCV rule is 60 characters) never fitted on a line anyway.
 *  - UNCONDITIONAL, spelled out, when there is no condition at all.
 *  - DEAD state. A branch sitting after a catch-all can never be taken, and that is a
 *    fact about the edge — the target node is usually still perfectly reachable
 *    through a different edge, so marking the node would point at the wrong thing.
 */
import type { CpaEdge } from '../../utils/projection';

/** Keeps an un-renderable expression from stretching the label across the canvas. */
const MAX_RAW = 28;

export function edgeLabelText(e: CpaEdge): string {
  if (e.displayLabel) return e.displayLabel;
  const raw = (e.label ?? '').trim();
  return raw.length > MAX_RAW ? `${raw.slice(0, MAX_RAW - 1)}…` : raw;
}

export default function EdgeLabel({ edge }: { edge: CpaEdge }) {
  /*
   * An unconditional edge is LABELLED, not left bare.
   *
   * It used to render nothing, which made "this transition is unconditional" look
   * exactly like "this transition has not been configured yet" — and on a surface whose
   * job is to make rules legible, those two must not share a picture. A line now always
   * says one of the two things it can be: a condition, or 无条件.
   */
  if (edge.kind === 'always') {
    return <span className="cpa-edge-label cpa-edge-label--always">无条件</span>;
  }
  const text = edgeLabelText(edge);
  if (!text) return null;
  const showOrder = (edge.ruleCount ?? 0) > 1 && edge.ruleIndex !== undefined;
  return (
    <span
      className={[
        'cpa-edge-label',
        edge.isDeadBranch ? 'cpa-edge-label--dead' : '',
        edge.isDefaultBranch ? 'cpa-edge-label--default' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      // The full expression, always, even when a short form was rendered — the short
      // form is a reading aid, not the truth.
      title={
        (edge.label ?? '') +
        (edge.isDeadBranch ? '\n⚠ 这条分支永远不会被取用（排在默认分支之后或与前一条重复）' : '')
      }
    >
      {showOrder && <b className="cpa-edge-label__ord">{edge.ruleIndex}</b>}
      {edge.isDeadBranch && <span className="cpa-edge-label__dead-mark">✕</span>}
      {text}
    </span>
  );
}
